import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Search, Upload, X } from "lucide-react";

/**
 * Mobile-optimized photo picker:
 *  [SEARCH UNSPLASH] — text input + results grid, click to pick top result
 *  [UPLOAD PHOTO]    — opens camera roll
 * Shows preview thumbnail immediately after selection.
 */
export default function PhotoPicker({ imageUrl, onChange, onCreditChange, credit, suggestedQuery, hidePreview }) {
  const [query, setQuery] = useState(suggestedQuery || "");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Keep query synced with suggestedQuery while the user hasn't typed anything
  useEffect(() => {
    setQuery((q) => (q ? q : suggestedQuery || ""));
  }, [suggestedQuery]);

  const searchUnsplash = async (autoPickTop = false) => {
    if (!query.trim()) {
      toast.error("Enter a destination to search");
      return;
    }
    setSearching(true);
    try {
      const { data } = await api.post("/admin/unsplash-search", { query: query.trim() });
      setResults(data.results || []);
      if (autoPickTop && data.results?.length) {
        const top = data.results[0];
        onChange(top.regular);
        onCreditChange?.(top.photographer ? `Photo by ${top.photographer} on Unsplash` : "");
        toast.success(`Top Unsplash result selected`);
      } else if (!data.results?.length) {
        toast.error("No photos found — try a different query");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Search failed");
    } finally {
      setSearching(false);
    }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-uploading same file
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.image_url);
      onCreditChange?.("");
      setResults([]);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const clear = () => {
    onChange("");
    onCreditChange?.("");
    setResults([]);
  };

  return (
    <div className="space-y-4" data-testid="photo-picker">
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
        <div className="relative flex items-stretch gap-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666] pointer-events-none z-10" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); searchUnsplash(true); } }}
            placeholder="e.g. bangkok temple"
            className="ed-input ed-input-lg flex-1 pl-10 !border-r-0"
            data-testid="unsplash-query-input"
            inputMode="search"
          />
          <button
            type="button"
            onClick={() => searchUnsplash(true)}
            disabled={searching}
            className="btn-rd !py-0 !px-4 h-[52px] !text-[0.7rem] whitespace-nowrap"
            data-testid="unsplash-search-btn"
          >
            <Search className="w-4 h-4" /> {searching ? "…" : "SEARCH UNSPLASH"}
          </button>
        </div>
        <label className="btn-rd-yellow h-[52px] !py-0 cursor-pointer justify-center" data-testid="photo-upload-label">
          <Upload className="w-4 h-4" /> {uploading ? "UPLOADING…" : "UPLOAD PHOTO"}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={uploadFile}
            data-testid="photo-upload-input"
          />
        </label>
      </div>

      {/* Result grid */}
      {results.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="unsplash-results">
          {results.map((r) => {
            const active = imageUrl === r.regular;
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => { onChange(r.regular); onCreditChange?.(r.photographer ? `Photo by ${r.photographer} on Unsplash` : ""); }}
                className={`relative aspect-square overflow-hidden border-2 transition-colors ${
                  active ? "border-[#CC1A1A]" : "border-transparent hover:border-[#FFDD57]"
                }`}
                data-testid={`unsplash-result-${r.id}`}
              >
                <img src={r.thumb} alt={r.alt} className="w-full h-full object-cover" loading="lazy" />
                {active && (
                  <span className="absolute top-1 right-1 rd-badge !text-[0.55rem] !px-1.5 !py-0.5">✓</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Credit input — shown when a photo is selected */}
      {imageUrl && (
        <div>
          <label className="block text-[10px] font-mono tracking-[0.2em] uppercase text-[#555] mb-1">
            Photo credit (optional)
          </label>
          <input
            type="text"
            value={credit || ""}
            onChange={(e) => onCreditChange?.(e.target.value)}
            placeholder="e.g. Courtesy of Trip.com"
            className="w-full bg-[#0E0E0E] border border-[#2A2A2A] text-[#E5E0D8] font-mono text-xs px-3 py-2 outline-none focus:border-[#CC1A1A] transition-colors"
            data-testid="photo-credit-input"
          />
        </div>
      )}

      {/* Preview — hidden when ImagePositioner is used as the preview */}
      {imageUrl && !hidePreview && (
        <div className="relative w-full max-w-sm" data-testid="photo-preview">
          <img
            src={imageUrl}
            alt="selected destination"
            className="w-full aspect-[4/5] object-cover border border-[#2A2A2A]"
          />
          <button
            type="button"
            onClick={clear}
            className="absolute top-2 right-2 w-8 h-8 bg-[#111111]/85 border border-[#3A3A3A] flex items-center justify-center hover:border-[#CC1A1A] hover:text-[#CC1A1A]"
            data-testid="photo-clear-btn"
            aria-label="Clear photo"
          >
            <X className="w-4 h-4" />
          </button>
          <span className="absolute bottom-2 left-2 rd-badge rd-badge-yellow !text-[0.55rem]">
            {imageUrl.startsWith("data:") ? "UPLOADED" : "UNSPLASH"}
          </span>
        </div>
      )}
    </div>
  );
}
